import { useState } from "react";
import { Loader2, ListChecks, Check, Type } from "lucide-react";
import adminApi from "../lib/adminApi";
import { Button } from "./ui/button";

const s = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Dry run first, apply only after the owner confirms: auto-written stories follow their duck's name (unnamed ducks take
// the story's). Hand-edited stories are only listed, never changed. Running it again changes nothing.
export const NameSync = () => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const run = async (apply) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/ducks/name-sync", { apply });
      setDone(apply ? data : null);
      setPreview(apply ? null : data);
    } catch {
      setError("The name sync could not run. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="name-sync" className="mt-3 rounded-2xl border border-border bg-card/70 px-4 py-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Type className="h-4 w-4 text-muted-foreground shrink-0" />
        <p className="text-sm flex-1 min-w-[220px]">
          <span className="font-semibold">One name per duck.</span>{" "}
          <span className="text-muted-foreground">Auto-written stories follow their duck's name. Stories the owner wrote by hand are listed, never changed.</span>
        </p>
        <Button data-testid="name-sync-preview" size="sm" variant="outline" className="rounded-full h-9 gap-1.5" disabled={busy} onClick={() => run(false)}>
          {busy && !preview ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ListChecks className="h-3.5 w-3.5" />} Preview (dry run)
        </Button>
      </div>
      {preview && (
        <div data-testid="name-sync-result" className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs font-mono2 text-amber-900">
          <p data-testid="name-sync-summary">
            {preview.would_update} of {s(preview.scanned, "story", "stories")} would be synced ({preview.named} unnamed take their story's name, {preview.stories} follow the duck's name) · {preview.hand_edited_count} hand-edited left as they are
          </p>
          <ul className="mt-1 space-y-0.5">
            {preview.examples.map((e) => <li key={e.id} data-testid={`name-sync-example-${e.id}`}>{e.from || "(no name)"} → {e.to}</li>)}
            {preview.hand_edited.map((h) => (
              <li key={h.id} data-testid={`name-sync-hand-${h.id}`}>{h.name}: hand-edited story says “{h.still_says || h.story_name}” (left unchanged)</li>
            ))}
          </ul>
          {preview.would_update > 0 && (
            <Button data-testid="name-sync-apply" size="sm" className="mt-2 rounded-full h-9 gap-1.5 font-bold" disabled={busy} onClick={() => run(true)}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Apply to {s(preview.would_update, "duck", "ducks")}
            </Button>
          )}
        </div>
      )}
      {done && <p data-testid="name-sync-done" className="mt-2 text-xs font-mono2 text-emerald-700">Applied: {s(done.updated, "duck", "ducks")} synced. Running it again changes nothing.</p>}
      {error && <p data-testid="name-sync-error" className="mt-2 text-xs text-rose-700">{error}</p>}
    </div>
  );
};
